import { getOpenAIAdsRuntime } from './openaiAdsTracking.js';

const safeLabel = (value) => typeof value === 'string' && value.length > 0 && value.length <= 128
  && !/[\u0000-\u001f\u007f]/.test(value);

// Keep each real color/size row distinct. Never allocate a line total to variants.
export function openAIAdsVariantRows(item) {
  if (!item || item.pricingError || !safeLabel(item.productSku)) return [];
  const matrices = item.sizeMatrices || (item.color && item.sizeMatrix ? { [item.color]: item.sizeMatrix } : null);
  if (!matrices || typeof matrices !== 'object' || Array.isArray(matrices)) return [];
  const rows = [];
  for (const [color, matrix] of Object.entries(matrices)) {
    if (!safeLabel(color) || !matrix || typeof matrix !== 'object' || Array.isArray(matrix)) return [];
    for (const [size, quantity] of Object.entries(matrix)) {
      if (!safeLabel(size) || !Number.isSafeInteger(quantity) || quantity < 0) return [];
      if (quantity) rows.push({ productSku: item.productSku, color, size, quantity });
    }
  }
  return rows;
}

const variantKey = (row) => JSON.stringify([row.productSku, row.color, row.size]);
const pixelRow = (row) => ({
  id: row.productSku,
  // Pixel has no variant_dict; the display label retains the actual dimensions.
  name: `${row.productSku} | ${row.color} | ${row.size}`,
  content_type: 'product', quantity: row.quantity,
});

export function buildOpenAIAdsCartAdditions(previousItems, nextItems) {
  if (!Array.isArray(previousItems) || !Array.isArray(nextItems)) return [];
  const previousById = new Map(previousItems.map((item) => [item.id, item]));
  const additions = [];
  for (const item of nextItems) {
    const previous = new Map(openAIAdsVariantRows(previousById.get(item.id))
      .map((row) => [variantKey(row), row.quantity]));
    for (const row of openAIAdsVariantRows(item)) {
      const increase = row.quantity - (previous.get(variantKey(row)) || 0);
      if (increase > 0) additions.push(pixelRow({ ...row, quantity: increase }));
    }
  }
  return additions;
}

export function trackOpenAIAdsCartAdditions(previousItems, nextItems, runtime = getOpenAIAdsRuntime()) {
  try {
    const contents = buildOpenAIAdsCartAdditions(previousItems, nextItems);
    return contents.length ? runtime?.measure('items_added', { type: 'contents', contents }) : false;
  } catch (_) { return false; }
}

export function trackOpenAIAdsProductView(product, navigationKey, runtime = getOpenAIAdsRuntime()) {
  try {
    if (!safeLabel(product?.sku)) return false;
    return runtime?.measure('contents_viewed', {
      type: 'contents', contents: [{ id: product.sku, content_type: 'product' }],
    }, { key: `${navigationKey}:${product.sku}` });
  } catch (_) { return false; }
}

export function trackOpenAIAdsCheckout({ items, episodeId, amount }, runtime = getOpenAIAdsRuntime()) {
  try {
    if (!Array.isArray(items) || !items.length || !safeLabel(episodeId)) return false;
    const contents = items.flatMap((item) => openAIAdsVariantRows(item).map(pixelRow));
    if (!contents.length) return false;
    const data = { type: 'contents', contents };
    const minorUnits = Math.round(amount * 100);
    if (typeof amount === 'number' && Number.isFinite(amount) && amount >= 0 && Number.isSafeInteger(minorUnits)) {
      data.amount = minorUnits;
      data.currency = 'ILS';
    }
    return runtime?.measure('checkout_started', data, { key: episodeId, once: true });
  } catch (_) { return false; }
}
